import { styled } from "goober";
import { useCallback, useMemo, useRef, useState } from "react";
import type { MonitoringConnectorUsageResponse } from "../../../shared/connectorUsageReport";
import { queries } from "../../../shared/queries";
import { useQuery } from "../../hooks/useQuery";
import { useRevisionedJson } from "../../hooks/useRevisionedJson";
import { useSpaceDisplayName } from "../../hooks/useSpaceDisplayName";
import { monitoringConnectorUsagePath } from "../../lib/apiPaths";
import { navigate, routes, useCurrentUrl } from "../../lib/urls";
import { ConnectorLogo } from "../ConnectorLogo";
import { Dropdown } from "../Dropdown";
import { Icon } from "../Icon";
import { Skeleton } from "../Skeleton";
import { Text } from "../Text";
import { useMonitoringInteraction } from "./analytics";
import {
  ConnectorRequestVolumePanel,
  type OverviewStat,
  type RequestVolumeSeries,
} from "./ConnectorRequestVolumePanel";
import { ConnectorBreakdownPanel } from "./ConnectorBreakdownPanel";
import {
  ConnectorVolumeBars,
  type ConnectorVolumeBar,
} from "./ConnectorVolumeBars";
import {
  computeConnectorSummaries,
  computeDailyVolume,
  computeTrafficOverview,
  computeWorkflowSummaries,
  connectorUsageSpaceIds,
  filterUsageRows,
  formatErrorRatePercent,
  formatRequestCount,
  NO_SPACE,
} from "./connectorTraffic";
import { ControlButton } from "./ControlButton";
import { MonitoringLayout } from "./MonitoringLayout";
import { MonitoringScopeSelector } from "./MonitoringScopeSelector";
import { SPACE_COLORS, spaceColorMap } from "./monitoringPanelLayout";
import {
  foldsPersonal,
  PERSONAL_BUCKET,
  PERSONAL_BUCKET_COLOR,
} from "./monitoringScope";
import { useMonitoringScope } from "./useMonitoringScope";
import { RefreshButton } from "./RefreshButton";
import { ScopeDot } from "./ScopeDot";
import { ConnectorsGrid, Content } from "./workflowsLayout";

const ALL_CONNECTORS = "__all__";
const ALL_SPACES = "__all_spaces__";
const OTHER_CONNECTOR = "__other__";

// Gray is the palette's reserved "other/none", so it carries both the folded
// connector tail and usage with no space.
const UNATTRIBUTED_COLOR = "var(--gray-600)";

const DAY = 86_400_000;
const dayFloor = (t: number) => Math.floor(t / DAY) * DAY;

// Windows are calendar ranges keyed by their start; some (this week/month)
// extend past today, which the chart draws as a dashed projection. The endpoint
// takes the resolved start/end day boundaries.
type WindowOption = { value: string; label: string; phrase: string };

const WINDOW_OPTIONS: WindowOption[] = [
  { value: "7", label: "7 days", phrase: "in the last 7 days" },
  { value: "30", label: "30 days", phrase: "in the last 30 days" },
  { value: "this_week", label: "This week", phrase: "this week" },
  { value: "last_week", label: "Last week", phrase: "last week" },
  { value: "this_month", label: "This month", phrase: "this month" },
  { value: "last_month", label: "Last month", phrase: "last month" },
];

const DEFAULT_WINDOW = WINDOW_OPTIONS.find((o) => o.value === "this_week")!;

function windowOption(param: string): WindowOption {
  return (
    WINDOW_OPTIONS.find((option) => option.value === param) ?? DEFAULT_WINDOW
  );
}

// Week starts Monday; all boundaries are UTC to align with the day-grained ledger.
function startOfWeek(day0: number): number {
  const sinceMonday = (new Date(day0).getUTCDay() + 6) % 7;
  return day0 - sinceMonday * DAY;
}
function startOfMonth(day0: number): number {
  const d = new Date(day0);
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1);
}
function endOfMonth(day0: number): number {
  const d = new Date(day0);
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0);
}

function windowRange(
  value: string,
  today0: number
): { start: number; end: number } {
  switch (value) {
    case "7":
      return { start: today0 - 6 * DAY, end: today0 };
    case "this_week": {
      const s = startOfWeek(today0);
      return { start: s, end: s + 6 * DAY };
    }
    case "last_week": {
      const s = startOfWeek(today0) - 7 * DAY;
      return { start: s, end: s + 6 * DAY };
    }
    case "this_month":
      return { start: startOfMonth(today0), end: endOfMonth(today0) };
    case "last_month": {
      const prev = startOfMonth(today0) - DAY;
      return { start: startOfMonth(prev), end: endOfMonth(prev) };
    }
    default:
      return { start: today0 - 29 * DAY, end: today0 };
  }
}

const PickerTrigger = styled(ControlButton)`
  max-width: 280px;
`;

function LoadingPlaceholder() {
  return (
    <Content aria-hidden>
      <ConnectorsGrid>
        <Skeleton
          style={{
            width: "100%",
            height: "100%",
            minHeight: 220,
            borderRadius: 12,
          }}
        />
        <Skeleton
          style={{
            width: "100%",
            height: "100%",
            minHeight: 220,
            borderRadius: 12,
          }}
        />
        <Skeleton
          style={{
            width: "100%",
            height: "100%",
            minHeight: 220,
            borderRadius: 12,
          }}
        />
      </ConnectorsGrid>
    </Content>
  );
}

export function ConnectorsPanel() {
  const recordInteraction = useMonitoringInteraction();
  const displaySpaceName = useSpaceDisplayName();
  const {
    window: windowParam = "this_week",
    space: spaceParam,
    connector: connectorParam,
  } = useCurrentUrl(routes.monitoring.connectors);
  const selectedWindow = windowOption(windowParam);
  const today0 = dayFloor(Date.now());
  const { start, end } = windowRange(selectedWindow.value, today0);

  const [refreshToken, setRefreshToken] = useState(0);
  // Read already collapsed rather than synced row by row, so the window only
  // moves on a refresh.
  const { data: usage, loading: usageLoading } =
    useRevisionedJson<MonitoringConnectorUsageResponse>(
      monitoringConnectorUsagePath(),
      `start=${start}&end=${end}`,
      refreshToken
    );
  // Named for the AI tab but defined as the relation-free connector list, so
  // both monitoring tabs share one hydration.
  const [connectors, connectorsQuery] = useQuery(queries.monitorAiConnectors());
  const [workflows, workflowsQuery] = useQuery(queries.allWorkflows());
  const [spaces, spacesQuery] = useQuery(
    queries.spacesLite({ includeAdminSpaces: true })
  );

  const isLoading =
    usageLoading ||
    connectorsQuery.type !== "complete" ||
    workflowsQuery.type !== "complete" ||
    spacesQuery.type !== "complete";

  // Held so a window change doesn't blank the panels mid-fetch. A failed fetch
  // resolves with no payload, and clearing on that reads as a tenant with no
  // traffic at all.
  const heldDaily = useRef(usage?.daily ?? []);
  const heldWorkflowRows = useRef(usage?.workflows ?? []);
  const heldSources = useRef(usage?.sources ?? []);
  if (!isLoading && usage) {
    heldDaily.current = usage.daily;
    heldWorkflowRows.current = usage.workflows;
    heldSources.current = usage.sources;
  }
  const allTrafficRows = heldDaily.current;
  const allWorkflowRows = heldWorkflowRows.current;
  const allSourceRows = heldSources.current;

  const { scope, personalIds, matchesSpace } = useMonitoringScope(
    routes.monitoring.connectors
  );
  const scopedTrafficRows = useMemo(
    () => allTrafficRows.filter((row) => matchesSpace(row.spaceId)),
    [allTrafficRows, matchesSpace]
  );
  const scopedWorkflowRows = useMemo(
    () => allWorkflowRows.filter((row) => matchesSpace(row.spaceId)),
    [allWorkflowRows, matchesSpace]
  );

  const connectorMeta = useMemo(() => {
    const map = new Map<string, { name: string; identifier: string }>();
    for (const connector of connectors ?? []) {
      map.set(connector.id, {
        name: connector.name,
        identifier: connector.identifier,
      });
    }
    return map;
  }, [connectors]);

  const allSummaries = useMemo(
    () => computeConnectorSummaries(scopedTrafficRows),
    [scopedTrafficRows]
  );

  const spaceOptions = useMemo(() => {
    const { spaceIds, hasUnattributed } =
      connectorUsageSpaceIds(scopedTrafficRows);
    const present = new Set(spaceIds);
    let hasPersonal = false;
    const options: { id: string; label: string }[] = [];
    for (const space of spaces ?? []) {
      if (!present.has(space.id) || !matchesSpace(space.id)) continue;
      if (foldsPersonal(scope) && personalIds.has(space.id)) {
        hasPersonal = true;
        continue;
      }
      options.push({ id: space.id, label: displaySpaceName(space) });
    }
    options.sort((a, b) => a.label.localeCompare(b.label));
    if (hasPersonal) options.push({ id: PERSONAL_BUCKET, label: "Personal" });
    return { options, hasUnattributed };
  }, [
    scopedTrafficRows,
    spaces,
    scope,
    personalIds,
    matchesSpace,
    displaySpaceName,
  ]);

  const spaceColors = useMemo(() => {
    const map = new Map(spaceColorMap(spaces ?? []));
    map.set(PERSONAL_BUCKET, PERSONAL_BUCKET_COLOR);
    return map;
  }, [spaces]);

  const spaceValid =
    spaceParam != null &&
    (spaceParam === NO_SPACE
      ? spaceOptions.hasUnattributed
      : spaceOptions.options.some((option) => option.id === spaceParam));
  const effectiveSpaceId = spaceValid ? spaceParam : null;

  const connectorValid =
    connectorParam != null &&
    allSummaries.some((summary) => summary.connectorId === connectorParam);
  const effectiveConnectorId = connectorValid ? connectorParam : null;

  // The synthetic "Personal" bucket (shown only under "both") matches any
  // personal space; every other selection is an exact space id or null.
  const filterForSpace = useCallback(
    <T extends { spaceId: string | null; connectorId: string }>(
      rows: readonly T[],
      connectorId: string | null
    ): readonly T[] =>
      effectiveSpaceId === PERSONAL_BUCKET
        ? filterUsageRows(
            rows.filter((r) => r.spaceId != null && personalIds.has(r.spaceId)),
            null,
            connectorId
          )
        : filterUsageRows(rows, effectiveSpaceId, connectorId),
    [effectiveSpaceId, personalIds]
  );

  const filteredTraffic = useMemo(
    () => filterForSpace(scopedTrafficRows, effectiveConnectorId),
    [filterForSpace, scopedTrafficRows, effectiveConnectorId]
  );
  const summaries = useMemo(
    () => computeConnectorSummaries(filteredTraffic),
    [filteredTraffic]
  );

  const workflowSummaries = useMemo(
    () =>
      computeWorkflowSummaries(
        filterForSpace(scopedWorkflowRows, effectiveConnectorId)
      ),
    [filterForSpace, scopedWorkflowRows, effectiveConnectorId]
  );
  // Deleted workflows are left out so the table falls back to "Deleted
  // workflow" and drops the link.
  const workflowsById = useMemo(() => {
    const map = new Map<
      string,
      {
        id: string;
        name: string;
        badgeSeed: string | null;
        pausedAt: number | null;
      }
    >();
    for (const workflow of workflows ?? []) {
      if (!workflow.deletedAt)
        map.set(workflow.id, {
          id: workflow.id,
          name: workflow.name,
          badgeSeed: workflow.badgeSeed,
          pausedAt: workflow.pausedAt,
        });
    }
    return map;
  }, [workflows]);
  const overview = useMemo(
    () => computeTrafficOverview(summaries),
    [summaries]
  );
  // Fill the whole window (end can be in the future for this week/month), so the
  // chart draws the dashed projection past today.
  const volume = useMemo(
    () => computeDailyVolume(filteredTraffic, start, end),
    [filteredTraffic, start, end]
  );

  // Non-workflow usage broken out by source, shown below the workflows in the
  // drill-in. Non-workflow usage belongs to no space, so a space filter excludes
  // it — matching how the connector/workflow totals reconcile.
  const unattributedSources = useMemo(() => {
    if (
      effectiveConnectorId == null ||
      effectiveSpaceId != null ||
      scope !== "both"
    )
      return [];
    return allSourceRows
      .filter((row) => row.connectorId === effectiveConnectorId)
      .map((row) => ({
        source: row.source,
        requests: row.requestCount,
        errors: row.errorCount,
      }))
      .filter((entry) => entry.requests > 0)
      .sort(
        (a, b) => b.requests - a.requests || a.source.localeCompare(b.source)
      );
  }, [allSourceRows, effectiveConnectorId, effectiveSpaceId, scope]);

  const connectorName = useCallback(
    (connectorId: string) =>
      connectorMeta.get(connectorId)?.name ?? "Deleted connector",
    [connectorMeta]
  );

  // The bar chart keeps every connector in the space scope visible (a connector
  // filter fades the unselected rather than dropping them), so it is scoped by
  // space but never by the selected connector.
  const spaceSummaries = useMemo(
    () => computeConnectorSummaries(filterForSpace(scopedTrafficRows, null)),
    [filterForSpace, scopedTrafficRows]
  );

  // One stable color per connector, ranked across the space-scoped set, so a
  // connector reads the same in the bars and the line-chart tooltip.
  const connectorColorById = useMemo(() => {
    const map = new Map<string, string>();
    spaceSummaries.forEach((summary, i) =>
      map.set(summary.connectorId, SPACE_COLORS[i % SPACE_COLORS.length]!)
    );
    return map;
  }, [spaceSummaries]);

  const volumeSeries = useMemo(() => {
    const coloredCount =
      summaries.length > SPACE_COLORS.length
        ? SPACE_COLORS.length - 1
        : summaries.length;
    const series: RequestVolumeSeries[] = [];
    const seriesKeyOf = new Map<string, string>();
    summaries.forEach((summary, i) => {
      if (i < coloredCount) {
        seriesKeyOf.set(summary.connectorId, summary.connectorId);
        series.push({
          key: summary.connectorId,
          label: connectorName(summary.connectorId),
          color: connectorColorById.get(summary.connectorId)!,
        });
      } else {
        seriesKeyOf.set(summary.connectorId, OTHER_CONNECTOR);
      }
    });
    const otherCount = summaries.length - coloredCount;
    if (otherCount > 0) {
      series.push({
        key: OTHER_CONNECTOR,
        label: `${otherCount} other connector${otherCount === 1 ? "" : "s"}`,
        color: UNATTRIBUTED_COLOR,
        isOther: true,
      });
    }
    return { series, seriesKeyOf };
  }, [summaries, connectorName, connectorColorById]);

  // Every connector in the space scope gets a bar (scrolling horizontally once
  // they overflow); a connector filter fades the unselected rather than
  // dropping them.
  const volumeBars = useMemo<ConnectorVolumeBar[]>(
    () =>
      spaceSummaries.map((summary) => ({
        key: summary.connectorId,
        connectorId: summary.connectorId,
        identifier:
          connectorMeta.get(summary.connectorId)?.identifier ?? "unknown",
        label: connectorName(summary.connectorId),
        requests: summary.requests,
        color: connectorColorById.get(summary.connectorId)!,
      })),
    [spaceSummaries, connectorMeta, connectorName, connectorColorById]
  );

  const setParam = (
    key: "window" | "space" | "connector",
    value: string | null,
    interaction:
      | "connectors.window_change"
      | "connectors.space_filter_change"
      | "connectors.connector_filter_change"
  ) => {
    recordInteraction(interaction, { value: value ?? undefined });
    navigate(
      routes.monitoring.connectors,
      { [key]: value ?? "" },
      { replace: true }
    );
  };

  const hasLoadedOnce = useRef(false);
  if (!isLoading) hasLoadedOnce.current = true;

  if (isLoading && !hasLoadedOnce.current) {
    return (
      <MonitoringLayout
        headerItem={
          <MonitoringScopeSelector route={routes.monitoring.connectors} />
        }
      >
        <LoadingPlaceholder />
      </MonitoringLayout>
    );
  }

  // No usage in the selected window renders a realistic, non-animated skeleton
  // of the populated tab (ghost line, bars, and rows) rather than a bare empty
  // card, matching the other monitoring tabs. The window picker stays live so a
  // different range can still surface data.
  const isEmpty = allSummaries.length === 0;

  const selectedCheck = (
    <Icon name="check" size={12} color="var(--text-secondary)" />
  );

  const connectorPicker = (
    <Dropdown
      align="bottom"
      opener={
        <PickerTrigger type="button">
          {effectiveConnectorId ? (
            <ConnectorLogo
              template={{
                name: connectorName(effectiveConnectorId),
                identifier:
                  connectorMeta.get(effectiveConnectorId)?.identifier ??
                  "unknown",
              }}
              size={16}
            />
          ) : (
            <Icon name="layers" size={16} color="var(--text-primary)" />
          )}
          <Text variant="text-small-md" ellipsis>
            {effectiveConnectorId
              ? connectorName(effectiveConnectorId)
              : "All connectors"}
          </Text>
          <Icon name="chevron-down" size={12} color="var(--text-secondary)" />
        </PickerTrigger>
      }
      sections={[
        {
          items: [
            {
              type: "button" as const,
              key: ALL_CONNECTORS,
              left: (
                <Icon name="layers" size={16} color="var(--text-primary)" />
              ),
              label: "All connectors",
              selected: effectiveConnectorId == null,
              right: effectiveConnectorId == null ? selectedCheck : undefined,
              onClick: () =>
                setParam(
                  "connector",
                  null,
                  "connectors.connector_filter_change"
                ),
            },
            ...allSummaries.map((summary) => ({
              type: "button" as const,
              key: summary.connectorId,
              left: (
                <ConnectorLogo
                  template={{
                    name: connectorName(summary.connectorId),
                    identifier:
                      connectorMeta.get(summary.connectorId)?.identifier ??
                      "unknown",
                  }}
                  size={16}
                />
              ),
              label: connectorName(summary.connectorId),
              selected: summary.connectorId === effectiveConnectorId,
              right:
                summary.connectorId === effectiveConnectorId
                  ? selectedCheck
                  : undefined,
              onClick: () =>
                setParam(
                  "connector",
                  summary.connectorId,
                  "connectors.connector_filter_change"
                ),
            })),
          ],
        },
      ]}
    />
  );

  const spacePicker = (
    <Dropdown
      align="bottom"
      opener={
        <PickerTrigger type="button">
          {effectiveSpaceId == null ? (
            <Icon name="layers" size={16} color="var(--text-primary)" />
          ) : (
            <ScopeDot
              style={{
                background:
                  effectiveSpaceId === NO_SPACE
                    ? UNATTRIBUTED_COLOR
                    : spaceColors.get(effectiveSpaceId),
              }}
            />
          )}
          <Text variant="text-small-md" ellipsis>
            {effectiveSpaceId == null
              ? "All spaces"
              : effectiveSpaceId === NO_SPACE
                ? "Unattributed"
                : (spaceOptions.options.find(
                    (option) => option.id === effectiveSpaceId
                  )?.label ?? "All spaces")}
          </Text>
          <Icon name="chevron-down" size={12} color="var(--text-secondary)" />
        </PickerTrigger>
      }
      sections={[
        {
          items: [
            {
              type: "button" as const,
              key: ALL_SPACES,
              left: (
                <Icon name="layers" size={16} color="var(--text-primary)" />
              ),
              label: "All spaces",
              selected: effectiveSpaceId == null,
              right: effectiveSpaceId == null ? selectedCheck : undefined,
              onClick: () =>
                setParam("space", null, "connectors.space_filter_change"),
            },
            ...spaceOptions.options.map((option) => ({
              type: "button" as const,
              key: option.id,
              left: (
                <ScopeDot style={{ background: spaceColors.get(option.id) }} />
              ),
              label: option.label,
              selected: option.id === effectiveSpaceId,
              right: option.id === effectiveSpaceId ? selectedCheck : undefined,
              onClick: () =>
                setParam("space", option.id, "connectors.space_filter_change"),
            })),
            ...(spaceOptions.hasUnattributed
              ? [
                  {
                    type: "button" as const,
                    key: NO_SPACE,
                    left: (
                      <ScopeDot style={{ background: UNATTRIBUTED_COLOR }} />
                    ),
                    label: "Unattributed",
                    selected: effectiveSpaceId === NO_SPACE,
                    right:
                      effectiveSpaceId === NO_SPACE ? selectedCheck : undefined,
                    onClick: () =>
                      setParam(
                        "space",
                        NO_SPACE,
                        "connectors.space_filter_change"
                      ),
                  },
                ]
              : []),
          ],
        },
      ]}
    />
  );

  const windowPicker = (
    <Dropdown
      align="bottom"
      opener={
        <PickerTrigger type="button">
          <Icon name="clock" size={16} color="var(--text-primary)" />
          <Text variant="text-small-md" ellipsis>
            {selectedWindow.label}
          </Text>
          <Icon name="chevron-down" size={12} color="var(--text-secondary)" />
        </PickerTrigger>
      }
      sections={[
        {
          items: WINDOW_OPTIONS.map((option) => ({
            type: "button" as const,
            key: option.value,
            left: <Icon name="clock" size={16} color="var(--text-primary)" />,
            label: option.label,
            selected: option.value === selectedWindow.value,
            right:
              option.value === selectedWindow.value ? selectedCheck : undefined,
            onClick: () =>
              setParam("window", option.value, "connectors.window_change"),
          })),
        },
      ]}
    />
  );

  const stats: OverviewStat[] = [
    { label: "Requests", value: overview.requests, format: formatRequestCount },
    { label: "Errors", value: overview.errors, format: formatRequestCount },
    {
      label: "Error rate",
      value:
        overview.requests === 0
          ? 0
          : (overview.errors / overview.requests) * 100,
      format: overview.requests === 0 ? () => "—" : formatErrorRatePercent,
    },
  ];

  const refreshButton = (
    <RefreshButton onClick={() => setRefreshToken((token) => token + 1)} />
  );

  return (
    <MonitoringLayout
      headerItem={
        <MonitoringScopeSelector route={routes.monitoring.connectors} />
      }
    >
      <Content>
        <ConnectorsGrid>
          <ConnectorRequestVolumePanel
            volume={volume}
            series={volumeSeries.series}
            seriesKeyOf={volumeSeries.seriesKeyOf}
            stats={stats}
            nowDay={today0}
            placeholder={isEmpty}
            filters={
              isEmpty ? (
                <>
                  {windowPicker}
                  {refreshButton}
                </>
              ) : (
                <>
                  {connectorPicker}
                  {spacePicker}
                  {windowPicker}
                  {refreshButton}
                </>
              )
            }
          />
          <ConnectorVolumeBars
            bars={volumeBars}
            selectedConnectorId={effectiveConnectorId}
            placeholder={isEmpty}
            onToggle={(id) =>
              setParam(
                "connector",
                id === effectiveConnectorId ? null : id,
                "connectors.connector_filter_change"
              )
            }
            description={`Requests per connector ${selectedWindow.phrase}.`}
          />
          <ConnectorBreakdownPanel
            connectorSummaries={summaries}
            workflowSummaries={workflowSummaries}
            connectorMeta={connectorMeta}
            workflowsById={workflowsById}
            windowPhrase={selectedWindow.phrase}
            selectedConnectorId={effectiveConnectorId}
            unattributedSources={unattributedSources}
            placeholder={isEmpty}
            onClearConnector={() =>
              setParam("connector", null, "connectors.connector_filter_change")
            }
          />
        </ConnectorsGrid>
      </Content>
    </MonitoringLayout>
  );
}
